/**
 * testDevice — Per-browser flag for admin's own dry-runs.
 *
 * When the admin marks the current browser as a "test device", every quiz
 * attempt / lead submission from this browser is tagged `is_test=true` on
 * the backend, and gets filtered out of every admin counter, lead list,
 * funnel breakdown, and sources attribution chart by default.
 *
 * Activation paths:
 *   1. Admin Panel toggle (`TestModeToggle`).
 *   2. URL parameter `?mark_test=1` (sets flag) or `?mark_test=0` (clears).
 *      Useful for marking a phone — open the link once and the flag persists.
 *
 * Implementation: a single localStorage key, `pirate_test_device`.
 */
const KEY = "pirate_test_device";

export function isTestDevice() {
  try {
    return localStorage.getItem(KEY) === "true";
  } catch {
    return false;
  }
}

export function setTestDevice(enabled) {
  try {
    if (enabled) {
      localStorage.setItem(KEY, "true");
    } else {
      localStorage.removeItem(KEY);
    }
    // Notify any mounted listeners (e.g. admin toggle UI)
    window.dispatchEvent(new CustomEvent("pirate-test-device-changed", { detail: { enabled: !!enabled } }));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

/**
 * Handle `?mark_test=1` / `?mark_test=0` URL parameters on page load.
 * Strips the param from the URL afterwards so it doesn't get bookmarked twice.
 * Returns the new state if changed, otherwise null.
 */
export function applyMarkTestFromUrl() {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("mark_test")) return null;
    const raw = params.get("mark_test");
    const enabled = raw === "1" || raw === "true" || raw === "yes";
    setTestDevice(enabled);
    // Clean the URL so subsequent reloads don't keep toggling
    params.delete("mark_test");
    const newQuery = params.toString();
    const newUrl = window.location.pathname + (newQuery ? `?${newQuery}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
    return enabled;
  } catch {
    return null;
  }
}
